import { Link } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { Page } from '@/components/Page'
import { formatKarachiDateTime, karachiBusinessDate } from '@/lib/dates'
import { formatPKR, grossProfit, sumPaisa } from '@/lib/money'
import { openWorkspaceDb } from '@/offline/db'
import { saleOutstanding } from '@/features/payments/outstanding'
import { getActiveWorkspaceId } from '@/features/workspace/workspace'

/** Live business dashboard computed from local (offline-first) Dexie data. */
export function DashboardPage() {
  const ws = getActiveWorkspaceId()

  const devices = useLiveQuery(
    async () => (ws ? openWorkspaceDb(ws).devices.where('workspace_id').equals(ws).toArray() : []),
    [ws],
  )
  const purchases = useLiveQuery(
    async () => (ws ? openWorkspaceDb(ws).purchases.where('workspace_id').equals(ws).toArray() : []),
    [ws],
  )
  const sales = useLiveQuery(
    async () => (ws ? openWorkspaceDb(ws).sales.where('workspace_id').equals(ws).toArray() : []),
    [ws],
  )
  const payments = useLiveQuery(
    async () => (ws ? openWorkspaceDb(ws).payments.where('workspace_id').equals(ws).toArray() : []),
    [ws],
  )
  const pending = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).outbox.count() : 0), [ws])

  const purchaseFor = (deviceId: string) => purchases?.find((p) => p.device_id === deviceId && !p.deleted_at)
  const inStock = (devices ?? []).filter((d) => d.status === 'in_stock' && !d.deleted_at)
  const stockCost = sumPaisa(inStock.map((d) => purchaseFor(d.id)?.cost_paisa ?? 0))

  const liveSales = (sales ?? []).filter((s) => !s.deleted_at && !s.returned_at)
  const realized = sumPaisa(
    liveSales.map((s) => {
      const p = purchases?.find((x) => x.id === s.purchase_id)
      const cost = (p?.cost_paisa ?? 0) + (p?.direct_cost_paisa ?? 0)
      return grossProfit(s.sale_amount_paisa, cost, [])
    }),
  )
  const receivables = sumPaisa(liveSales.map((s) => saleOutstanding(s, payments ?? [])))

  const stats: Array<{ label: string; value: string; tone?: string }> = [
    { label: 'Stock value', value: formatPKR(stockCost) },
    { label: 'Units in stock', value: String(inStock.length) },
    { label: 'Realized profit', value: formatPKR(realized), tone: realized > 0 ? 'text-emerald-400' : undefined },
    { label: 'Receivables', value: formatPKR(receivables), tone: receivables > 0 ? 'text-amber-400' : undefined },
  ]

  return (
    <Page title="Home" note={`${karachiBusinessDate()} • ${formatKarachiDateTime(new Date())}`}>
      <div className="grid grid-cols-2 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="rounded-xl border border-slate-800 bg-slate-900/40 px-3 py-3">
            <p className="text-[11px] uppercase tracking-wide text-slate-500">{s.label}</p>
            <p className={`mt-1 text-lg font-semibold tabular-nums ${s.tone ?? 'text-slate-100'}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <Link
        to="/sync"
        className="mt-3 flex items-center justify-between rounded-xl border border-slate-800 bg-slate-900/40 px-3 py-2.5 text-sm"
      >
        <span className="text-slate-400">Sync & conflicts</span>
        <span className={pending ? 'text-amber-400' : 'text-emerald-400'}>
          {pending ? `${pending} pending` : 'All changes synced'}
        </span>
      </Link>

      {inStock.length === 0 && liveSales.length === 0 ? (
        <div className="mt-4 rounded-xl border border-dashed border-slate-700 px-4 py-6 text-center">
          <p className="text-sm text-slate-400">No stock yet.</p>
          <Link to="/inventory/new" className="mt-2 inline-block rounded-lg bg-sky-500 px-4 py-2 text-sm font-semibold text-white">
            Add your first purchase
          </Link>
        </div>
      ) : null}
    </Page>
  )
}
